import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { SalvButton, SalvDropdown, SalvProgressCircle, SalvTextArea, Size } from '@salvapp/ui-components';
import SalvPage from '../common/SalvPage';
import { getCasePath, InternalNavigation } from '../navigation/helper';
import { useCreateCase, useQueryCaseRationaleOptions } from './caseQueryActions';
import { CAN_CREATE_CASE } from '../auth/permissions';
import Has from '../auth/Has';
import { useDispatch } from 'react-redux';
import { showFeedback } from '../feedback/feedbackReducer';
import { highlightInput } from '../common/helper';
import DataLoadingErrorFallback from '../common/errors/DataLoadingErrorFallback';

export interface CaseFormValues {
  name: string;
  description?: string;
  rationaleCode: string;
}

export const validationSchema: Yup.Schema<CaseFormValues> = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  description: Yup.string(),
  rationaleCode: Yup.string().required('Rationale is required'),
});

const CaseCreation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const createCase = useCreateCase();
  const {
    data: rationaleOptions,
    isPending: isRationaleOptionsPending,
    isError: isRationaleOptionsError,
  } = useQueryCaseRationaleOptions();

  const submit = async (values: CaseFormValues) => {
    const createdCase = await createCase.mutateAsync({
      name: values.name,
      description: values.description || undefined,
      rationaleCode: values.rationaleCode,
    });
    dispatch(showFeedback({ message: 'Case created successfully', success: true }));
    navigate(getCasePath(createdCase.handle), { replace: true });
  };

  const nameInput = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (nameInput.current && !isRationaleOptionsPending) {
      highlightInput(nameInput.current);
    }
  }, [nameInput.current, isRationaleOptionsPending]);

  if (isRationaleOptionsError) {
    return <DataLoadingErrorFallback message={'Could not load Case Creation'} />;
  } else if (isRationaleOptionsPending) {
    return <SalvProgressCircle ariaLabel={'Loading Case Creation'} />;
  }

  return (
    <SalvPage title='New Case' breadcrumb={{ title: 'Cases', to: InternalNavigation.cases }}>
      <Formik
        initialValues={{} as CaseFormValues}
        validationSchema={validationSchema}
        validateOnMount
        onSubmit={submit}
      >
        {({ values, setFieldValue, isValid, isSubmitting }) => (
          <Form>
            <div style={{ maxWidth: '600px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <SalvTextArea
                ref={nameInput}
                label='Name'
                value={values.name}
                onChange={(value) => setFieldValue('name', value)}
                placeholder='Enter case name'
                required
                autoSizing
                minRows={1}
                size={Size.SMALL}
              />
              <SalvTextArea
                label='Description'
                value={values.description || ''}
                onChange={(value) => setFieldValue('description', value)}
                placeholder='Enter case description'
                autoSizing
                minRows={2}
                size={Size.SMALL}
              />
              <SalvDropdown
                label='Rationale'
                options={rationaleOptions}
                value={
                  values.rationaleCode
                    ? rationaleOptions.find((option) => option.value === values.rationaleCode)
                    : undefined
                }
                onChange={([option]) => setFieldValue('rationaleCode', option.value)}
                clearable={false}
                required
                size={Size.SMALL}
              />
              <div style={{ display: 'flex', gap: '8px' }}>
                <SalvButton
                  type='button'
                  variant='muted'
                  label='Cancel'
                  onClick={() => navigate(InternalNavigation.cases)}
                />
                <Has allPermissions={[CAN_CREATE_CASE]}>
                  <SalvButton type='submit' variant='primary' label='Create' disabled={isSubmitting || !isValid} />
                </Has>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </SalvPage>
  );
};

export default CaseCreation;
